import { useCallback, useRef, useEffect } from 'react'
import { Capacitor } from '@capacitor/core'

interface UseNotificationsOptions {
  onPermissionChange?: (permission: NotificationPermission) => void
}

interface UseNotificationsReturn {
  permission: NotificationPermission
  requestPermission: () => Promise<NotificationPermission>
  showNotification: (title: string, options?: NotificationOptions) => Notification | null
  isSupported: boolean
}

/**
 * Hook for browser notifications (Web Notifications API).
 * Handles permission requests and showing notifications.
 */
export function useNotifications({
  onPermissionChange,
}: UseNotificationsOptions = {}): UseNotificationsReturn {
  const isSupported = typeof Notification !== 'undefined' &&
    (!Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'electron')
  const permissionRef = useRef<NotificationPermission>(
    isSupported ? Notification.permission : 'denied'
  )

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      return 'denied'
    }

    try {
      const permission = await Notification.requestPermission()
      permissionRef.current = permission
      onPermissionChange?.(permission)
      return permission
    } catch {
      return 'denied'
    }
  }, [isSupported, onPermissionChange])

  const showNotification = useCallback(
    (title: string, options?: NotificationOptions): Notification | null => {
      if (!isSupported || permissionRef.current !== 'granted') {
        return null
      }

      try {
        const notification = new Notification(title, {
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          ...options,
        })

        return notification
      } catch {
        return null
      }
    },
    [isSupported]
  )

  // Check permission on mount
  useEffect(() => {
    if (isSupported) {
      permissionRef.current = Notification.permission
    }
  }, [isSupported])

  return {
    permission: permissionRef.current,
    requestPermission,
    showNotification,
    isSupported,
  }
}

/** Bundled chime used when no custom sound is configured. */
const DEFAULT_ALERT_SOUND_URL = '/sounds/new-order.wav'

/**
 * Hook for playing audio alerts.
 */
export function useAudioAlert() {
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const playAlert = useCallback((soundUrl: string = DEFAULT_ALERT_SOUND_URL) => {
    try {
      // Create or reuse audio element
      if (!audioRef.current) {
        audioRef.current = new Audio(soundUrl)
      } else if (!audioRef.current.src.endsWith(soundUrl)) {
        audioRef.current.src = soundUrl
      }

      audioRef.current.currentTime = 0
      audioRef.current.play().catch(() => {
        // Audio play failed (likely due to autoplay restrictions)
        // User needs to interact with page first
      })
    } catch {
      // Audio not supported
    }
  }, [])

  const stopAlert = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }, [])

  return { playAlert, stopAlert }
}
